<template>

  <div>
    <template v-for="(category, index) in downloads" :key="index">
      <div class="etx-container Container" v-if="downloads?.length > 0"
        :class="[paragraphClassList, { 'bg-secondary': index === 0 }]">
        <div class="etx-center Center">

          <div v-if="index === 0" class="etx-text Text" :class="entranceClassList">
            <p class="font-h2">{{ category?.name }}</p>
          </div>

          <div v-if="index === 0" class="etx-spacer Spacer h-50"></div>

          <div class="etx-flex Flex gv-200 tab-l:gv-0">

            <div class="etx-flex__item Flex__item tab-s:c-10 tab-m:c-9 tab-l:c-4">

              <div v-if="index != 0" class="etx-text Text" :class="entranceClassList">
                <p class="font-h4 font-color-primary">{{ category?.name }}</p>
              </div>

              <div v-if="index != 0" class="etx-spacer Spacer h-25"></div>

              <div v-if="category?.downloads?.some(download => download?.type?.isHighlight)"
                class="etx-item-list ItemList download">

                <a v-for="download in category.downloads.filter(d => d?.type?.isHighlight)"
                  class=" etx-item Item ItemLink" :href="download?.url?.path" :title="download?.name" target="_blank"
                  :class="entranceClassList">
                  <div class="etx-item__icon Item__icon">

                    <div class="etx-icon Icon larger">
                      <SpriteSymbol :name="index === 0
                        ? 'icons_Sammlung_negativ_Broschuere_offen'
                        : 'icons_Sammlung_positive_Broschuere_offen'" style="width:3.5rem;height:3.5rem" />
                    </div>

                  </div>

                  <div class="etx-item__text Item__text">
                    <p class="font-weight-bold font-size-large">{{ download?.name }}</p>

                    <div class="etx-button Button minimal">
                      <div class="etx-button__text Button__text">
                        <span>{{ downloadLabel }}</span>
                      </div>
                      <div class="etx-button__icon Button__icon">
                        <i class="far fa-arrow-down-to-bracket"></i>
                      </div>
                    </div>
                  </div>
                </a>

              </div>

            </div>

            <div class="etx-flex__item Flex__item tab-l:c-7 desk-s:c-6 tab-l:ml-1">

              <div v-if="category?.downloads?.some(download => !download?.type?.isHighlight)" class="DownloadList"
                :class="entranceClassList">

                <div class="DownloadList__header">

                  <div class="etx-text Text">
                    <p class="font-size-small font-color-medium">{{ $texts('downloads_listing.factsheets', 'Factsheets')
                      }}
                    </p>
                  </div>

                </div>

                <div class="DownloadList__content">

                  <div v-for="download in category.downloads.filter(d => !d?.type?.isHighlight)" class="Download">

                    <a class="Download__link" :href="download?.url?.path" :title="download?.name" target="_blank"></a>

                    <div class="Download__content">

                      <span class="font-weight-bold">{{ download?.name }}</span>
                      <!-- Todo download type here -->
                      <span v-if="download?.description" class="font-size-small font-color-medium">{{
                        download?.description }}</span>

                    </div>

                    <div class="Download__button">

                      <div class="etx-button Button minimal">
                        <div class="etx-button__text Button__text">
                          <span>{{ downloadLabel }}</span>
                        </div>
                        <div class="etx-button__icon Button__icon">
                          <i class="far fa-arrow-down-to-bracket"></i>
                        </div>
                      </div>

                    </div>

                  </div>

                </div>

              </div>

            </div>

          </div>

        </div>
      </div>

      <div class="etx-container Container" :class="paragraphClassList" v-else-if="isEditing">
        <div class="etx-center Center">
          <p class="editor-placeholder">
            {{ noDownloadsText }}
          </p>
        </div>
      </div>
    </template>
  </div>

</template>

<script lang="ts" setup>
import type { integer } from '@elastic/elasticsearch/lib/api/types';

const { $texts } = useNuxtApp()
const downloadLabel = computed(() => $texts('downloads_listing.download', 'Download'))
const noDownloadsText = computed(() => $texts('downloads_listing.no_downloads', 'Keine Downloads verfügbar. Bitte überprüfen Sie, ob die Downloads vorhanden sind und eine Kategorie haben.'))

const { options, isEditing } = defineBlokkli({
  bundle: 'downloads_listing',
  globalOptions: ['spacingTop', 'spacingBottom'],
  editor: {
    addBehaviour: 'no-form',
    disableEdit: true,
    maxInstances: 1,
  }
})

const paragraphClassList = computed(() => {

  const classList = []

  if (options.value.spacingTop) {
    classList.push(options.value.spacingTop)
  }

  if (options.value.spacingBottom) {
    classList.push(options.value.spacingBottom)
  }

  return classList
})

const entranceClassList = computed(() => {

  const classList = []

  if (!isEditing) {
    classList.push('will-appear', 'move-up')
  }

  return classList
})

const tenantId = useTenant();
const { data: downloads } = await useAsyncData(`allDownloads-${tenantId}`, async () => {
  const parent = tenantId === 'equans' ? '503' : '504' // not good, but hey
  const allDownloads = await useGraphqlQuery('allDownloads', { parent: parent }).then((v) => v?.data?.entityQuery?.items as Array<{
    name: string
    description: string
    category: {
      name: string
      weight: integer
    }
    type: {
      name: string
      weight: integer
      isHighlight: boolean
    }
    url: {
      path: string
    }
  }>)

  const groupedDownloads = Object.values(allDownloads.reduce((acc: { [key: string]: any }, download) => {
    const category = download.category?.name
    if (!category) {
      return acc
    }
    if (!acc[category]) {
      acc[category] = {
        ...download.category,
        downloads: []
      }
    }
    acc[category].downloads.push(download)
    return acc
  }, {}))

  /*
    // Sort each category's downloads by type weight (TODO CURRENTLY MISSING)
    Object.values(groupedDownloads).forEach((category: any) => {
      category.downloads.sort((a: any, b: any) => a.type.weight - b.type.weight)
    })

    Object.values(groupedDownloads).sort((a, b) => a.weight - b.weight)
  */

  return groupedDownloads
})

</script>
